import { 
  AppError, 
  BaseError, 
  ErrorCategory, 
  ErrorSeverity, 
  ErrorContext, 
  RecoveryAction,
  NetworkError,
  AuthenticationError,
  AuthorizationError,
  ValidationError,
  StorageError,
  DatabaseError,
  BusinessLogicError,
  ExternalApiError
} from '../types/error';

/**
 * Factory class for creating typed application errors
 */
export class ErrorFactory {
  private static getDefaultContext(): ErrorContext {
    return {
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: Math.random().toString(36).substring(7)
    };
  }

  private static createBaseError(
    code: string,
    category: ErrorCategory,
    severity: ErrorSeverity,
    userMessage: string,
    technicalMessage: string,
    recoverable: boolean = true,
    retryable: boolean = false,
    context?: Partial<ErrorContext>
  ): BaseError {
    const error = new Error(technicalMessage) as BaseError;
    error.name = 'AppError';
    error.code = code;
    error.category = category;
    error.severity = severity;
    error.userMessage = userMessage;
    error.technicalMessage = technicalMessage;
    error.recoverable = recoverable;
    error.retryable = retryable;
    error.context = { ...this.getDefaultContext(), ...context };
    return error;
  }

  static createNetworkError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    statusCode?: number,
    endpoint?: string,
    method?: string,
    timeout: boolean = false,
    context?: Partial<ErrorContext>
  ): NetworkError {
    const error = this.createBaseError(
      code,
      ErrorCategory.NETWORK,
      statusCode && statusCode >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM,
      userMessage,
      technicalMessage,
      true,
      true,
      context
    ) as NetworkError;

    error.statusCode = statusCode;
    error.endpoint = endpoint;
    error.method = method;
    error.timeout = timeout;

    // Add appropriate recovery actions
    error.recoveryActions = [
      {
        type: 'retry',
        label: 'Try Again',
        description: 'Retry the operation',
        icon: 'bi-arrow-clockwise'
      },
      {
        type: 'refresh',
        label: 'Refresh Page',
        description: 'Refresh the page and try again',
        icon: 'bi-arrow-clockwise'
      }
    ];

    if (timeout) {
      error.recoveryActions.unshift({
        type: 'manual_action',
        label: 'Check Connection',
        description: 'Check your internet connection',
        icon: 'bi-wifi'
      });
    }

    return error;
  }

  static createAuthenticationError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    authProvider?: string,
    expired: boolean = false,
    context?: Partial<ErrorContext>
  ): AuthenticationError {
    const error = this.createBaseError(
      code,
      ErrorCategory.AUTHENTICATION,
      ErrorSeverity.HIGH,
      userMessage,
      technicalMessage,
      true,
      false,
      context
    ) as AuthenticationError;

    error.authProvider = authProvider;
    error.expired = expired;

    error.recoveryActions = [
      {
        type: 'redirect',
        label: 'Log In',
        description: 'Go to login page',
        url: '/login',
        icon: 'bi-box-arrow-in-right'
      }
    ];

    if (expired) {
      error.recoveryActions.unshift({
        type: 'retry',
        label: 'Refresh Session',
        description: 'Try to refresh your session',
        icon: 'bi-arrow-clockwise'
      });
    }

    return error;
  }

  static createAuthorizationError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    requiredPermissions?: string[],
    userPermissions?: string[],
    context?: Partial<ErrorContext>
  ): AuthorizationError {
    const error = this.createBaseError(
      code,
      ErrorCategory.AUTHORIZATION,
      ErrorSeverity.MEDIUM,
      userMessage,
      technicalMessage,
      false,
      false,
      context
    ) as AuthorizationError;

    error.requiredPermissions = requiredPermissions;
    error.userPermissions = userPermissions;

    error.recoveryActions = [
      {
        type: 'redirect',
        label: 'Go Back',
        description: 'Return to previous page',
        icon: 'bi-arrow-left'
      },
      {
        type: 'contact_support',
        label: 'Contact Support',
        description: 'Request access permissions',
        icon: 'bi-envelope'
      }
    ];

    return error;
  }

  static createValidationError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    field?: string,
    validationRule?: string,
    expectedFormat?: string,
    actualValue?: any,
    context?: Partial<ErrorContext>
  ): ValidationError {
    const error = this.createBaseError(
      code,
      ErrorCategory.VALIDATION,
      ErrorSeverity.LOW,
      userMessage,
      technicalMessage,
      true,
      false,
      context
    ) as ValidationError;

    error.field = field;
    error.validationRule = validationRule;
    error.expectedFormat = expectedFormat;
    error.actualValue = actualValue;

    error.recoveryActions = [
      {
        type: 'manual_action',
        label: 'Fix Input',
        description: 'Correct the input and try again',
        icon: 'bi-pencil'
      }
    ];

    return error;
  }

  static createStorageError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    operation?: 'upload' | 'download' | 'delete' | 'move',
    fileName?: string,
    fileSize?: number,
    bucketName?: string,
    context?: Partial<ErrorContext>
  ): StorageError {
    const error = this.createBaseError(
      code,
      ErrorCategory.STORAGE,
      ErrorSeverity.MEDIUM,
      userMessage,
      technicalMessage,
      true,
      true,
      context
    ) as StorageError;

    error.operation = operation;
    error.fileName = fileName;
    error.fileSize = fileSize;
    error.bucketName = bucketName;

    error.recoveryActions = [
      {
        type: 'retry',
        label: 'Try Again',
        description: 'Retry the operation',
        icon: 'bi-arrow-clockwise'
      }
    ];

    if (operation === 'upload' && fileSize && fileSize > 1024 * 1024) {
      error.recoveryActions.push({
        type: 'manual_action',
        label: 'Reduce File Size',
        description: 'Try uploading a smaller file',
        icon: 'bi-file-earmark-arrow-down'
      });
    }

    return error;
  }

  static createDatabaseError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    table?: string,
    operation?: 'select' | 'insert' | 'update' | 'delete' | 'rpc',
    constraint?: string,
    context?: Partial<ErrorContext>
  ): DatabaseError {
    const error = this.createBaseError(
      code,
      ErrorCategory.DATABASE,
      ErrorSeverity.HIGH,
      userMessage,
      technicalMessage,
      true,
      true,
      context
    ) as DatabaseError;

    error.table = table;
    error.operation = operation;
    error.constraint = constraint;

    error.recoveryActions = [
      {
        type: 'retry',
        label: 'Try Again',
        description: 'Retry the operation',
        icon: 'bi-arrow-clockwise'
      },
      {
        type: 'refresh',
        label: 'Refresh Page',
        description: 'Refresh the page and try again',
        icon: 'bi-arrow-clockwise'
      }
    ];

    if (constraint) {
      error.recoveryActions.unshift({
        type: 'manual_action',
        label: 'Check Data',
        description: 'Verify your input data',
        icon: 'bi-check2-square'
      });
    }

    return error;
  }

  static createBusinessLogicError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    businessRule?: string,
    currentState?: any,
    expectedState?: any,
    context?: Partial<ErrorContext>
  ): BusinessLogicError {
    const error = this.createBaseError(
      code,
      ErrorCategory.BUSINESS_LOGIC,
      ErrorSeverity.MEDIUM,
      userMessage,
      technicalMessage,
      true,
      false,
      context
    ) as BusinessLogicError;

    error.businessRule = businessRule;
    error.currentState = currentState;
    error.expectedState = expectedState;

    error.recoveryActions = [
      {
        type: 'manual_action',
        label: 'Review Requirements',
        description: 'Check the requirements and try again',
        icon: 'bi-list-check'
      }
    ];

    return error;
  }

  static createExternalApiError(
    code: string,
    userMessage: string,
    technicalMessage: string,
    apiName?: string,
    endpoint?: string,
    statusCode?: number,
    apiErrorCode?: string,
    context?: Partial<ErrorContext>
  ): ExternalApiError {
    const error = this.createBaseError(
      code,
      ErrorCategory.EXTERNAL_API,
      statusCode && statusCode >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM,
      userMessage,
      technicalMessage,
      true,
      true,
      context
    ) as ExternalApiError;

    error.apiName = apiName;
    error.endpoint = endpoint;
    error.statusCode = statusCode;
    error.apiErrorCode = apiErrorCode;

    error.recoveryActions = [
      {
        type: 'retry',
        label: 'Try Again',
        description: 'Retry the operation',
        icon: 'bi-arrow-clockwise'
      }
    ];

    if (statusCode && statusCode >= 500) {
      error.recoveryActions.push({
        type: 'contact_support',
        label: 'Contact Support',
        description: 'The external service is experiencing issues',
        icon: 'bi-envelope'
      });
    }

    return error;
  }

  /**
   * Create error from unknown error object
   */
  static fromUnknown(
    error: unknown,
    code: string = 'UNKNOWN_ERROR',
    defaultUserMessage: string = 'An unexpected error occurred',
    context?: Partial<ErrorContext>
  ): AppError {
    if (this.isAppError(error)) {
      return error;
    }

    let technicalMessage = 'Unknown error';
    let originalError: Error | undefined;

    if (error instanceof Error) {
      technicalMessage = error.message;
      originalError = error;
    } else if (typeof error === 'string') {
      technicalMessage = error;
    } else if (error && typeof error === 'object') {
      technicalMessage = JSON.stringify(error);
    }

    const appError = this.createBaseError(
      code,
      ErrorCategory.UNKNOWN,
      ErrorSeverity.MEDIUM,
      defaultUserMessage,
      technicalMessage,
      true,
      false,
      context
    );

    appError.originalError = originalError;
    appError.recoveryActions = [
      {
        type: 'retry',
        label: 'Try Again',
        description: 'Try the operation again',
        icon: 'bi-arrow-clockwise'
      },
      {
        type: 'contact_support',
        label: 'Contact Support',
        description: 'Contact support if the problem persists',
        icon: 'bi-envelope'
      }
    ];

    return appError;
  }

  /**
   * Type guard to check if error is an AppError
   */
  static isAppError(error: unknown): error is AppError {
    return error instanceof Error && 'category' in error && 'severity' in error;
  }

  /**
   * Create error from HTTP response
   */
  static fromHttpResponse(
    response: Response,
    userMessage?: string,
    context?: Partial<ErrorContext>
  ): NetworkError {
    const code = `HTTP_${response.status}`;
    const defaultUserMessage = response.status >= 500 
      ? 'Server error occurred. Please try again later.'
      : 'Request failed. Please check your input and try again.';

    return this.createNetworkError(
      code,
      userMessage || defaultUserMessage,
      `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      response.url,
      undefined,
      false,
      context
    );
  }

  /**
   * Create error from Supabase error
   */
  static fromSupabaseError(
    error: any,
    operation?: string,
    context?: Partial<ErrorContext>
  ): AppError {
    const code = error.code || 'SUPABASE_ERROR';
    
    // Handle specific Supabase error codes
    if (error.code === 'PGRST116') {
      return this.createDatabaseError(
        'RECORD_NOT_FOUND',
        'The requested item was not found.',
        error.message,
        undefined,
        'select',
        undefined,
        context
      );
    }

    if (error.code === '23505') {
      return this.createDatabaseError(
        'DUPLICATE_RECORD',
        'This item already exists.',
        error.message,
        undefined,
        'insert',
        'unique',
        context
      );
    }

    if (error.code === '23503') {
      return this.createDatabaseError(
        'FOREIGN_KEY_VIOLATION',
        'Unable to complete operation due to data dependencies.',
        error.message,
        undefined,
        undefined,
        'foreign_key',
        context
      );
    }

    if (error.code === 'PGRST202') {
      return this.createDatabaseError(
        'FUNCTION_NOT_FOUND',
        'Database operation is not available.',
        error.message,
        undefined,
        'rpc',
        undefined,
        context
      );
    }

    // Default to database error
    return this.createDatabaseError(
      code,
      'Database operation failed. Please try again.',
      error.message,
      undefined,
      operation as any,
      undefined,
      context
    );
  }
}
